/**
 * Canonical "turn whatever was thrown into a string" helper.
 *
 * Ladder: `Error` → its `.message` (even when empty — the inline ternary it
 * replaces never fell back on an empty message, so neither does this);
 * a thrown `string` → itself; an error-shaped object with a string
 * `message` → that message; anything else (`undefined`, numbers, arrays,
 * objects without a message, hostile proxies) → `fallback`.
 *
 * Leaf module by contract: zero imports, no `server-only`, so it is safe
 * from client components, API routes, workflows, scripts, AND `convex/`
 * (18+ inline copies live under `convex/`; `tsc --project convex` gates the
 * production deploy, so this file must never grow an import).
 *
 * What it deliberately does NOT do: unwrap `ConvexError.data` — expected
 * user-facing Convex failures go through `getConvexErrorMessage` in
 * `lib/errors/app-error.ts` first (AGENTS.md § Convex Error Pattern); this
 * helper is the generic second step. It also never builds
 * `${name}: ${message}` — the 2026-09 audit found that shape inline only
 * twice, so there is no `describeError` sibling.
 *
 * Replaces the inline `error instanceof Error ? error.message : <fallback>`
 * ternary (790 non-test copies at audit time — lib 384, app 149, scripts 120,
 * components 56, apps/admin 51, convex 24, hooks 6; unit U3.2) and retires
 * the fallback-less canonical `lib/automations/utils.ts:getErrorMessage`
 * plus the local `getErrorMessage`/`toErrorMessage`/`errorMessage` copies in
 * `lib/chat-title.ts`, `lib/agents/prompts/prompt-autocomplete.ts`,
 * `lib/org-block/flow.ts`, `convex/metaConversions/actions.ts`, and
 * `app/(internal)/test/image-gen/page.tsx`. The codemod is
 * `scripts/codemods/error-message.ts`; the `String(error)` fallback family
 * rewrites to `getErrorMessage(error, String(error))` so the result for a
 * thrown number/null stays byte-identical.
 */
export function getErrorMessage(
  error: unknown,
  fallback = 'Unknown error'
): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  if (typeof error === 'object' && error !== null) {
    try {
      const message: unknown = (error as { message?: unknown }).message
      if (typeof message === 'string') {
        return message
      }
    } catch {
      // A hostile getter/proxy must never turn error reporting into a throw.
    }
  }
  return fallback
}
